import { ensureAdultingGoogleCalendar, isGoogleCalendarConfigured } from '@/lib/google-calendar';
import { ensureAdultingDriveStructure, isGoogleDriveConfigured } from '@/lib/google-drive';
import {
  loadIntegrationSettings,
  saveIntegrationSettings,
  type CalendarIntegrationMode,
  type DocumentIntegrationMode,
  type IntegrationSettingsRecord,
} from '@/lib/integration-settings';

export type SetupDocumentsMode = Extract<DocumentIntegrationMode, 'adulting' | 'google_drive' | 'icloud'>;
export type SetupCalendarMode = Extract<CalendarIntegrationMode, 'adulting' | 'google_calendar' | 'icloud'>;

export type IntegrationSetupNoticeTone = 'success' | 'info';

export type IntegrationSetupNotice = {
  tone: IntegrationSetupNoticeTone;
  message: string;
};

export type IntegrationSetupResult = {
  settings: IntegrationSettingsRecord | null;
  notice?: IntegrationSetupNotice;
};

async function resolveCurrentSettings(
  userId: string,
  currentSettings?: IntegrationSettingsRecord | null,
) {
  return currentSettings ?? loadIntegrationSettings(userId);
}

function resolveGoogleAccountEmail(
  settings: IntegrationSettingsRecord,
  nextDocumentsMode: IntegrationSettingsRecord['documents_mode'],
  nextCalendarMode: IntegrationSettingsRecord['calendar_mode'],
) {
  return nextDocumentsMode === 'google_drive' || nextCalendarMode === 'google_calendar'
    ? settings.google_account_email || null
    : null;
}

export async function updateSetupDocumentsMode({
  userId,
  mode,
  childNames,
}: {
  userId: string;
  mode: SetupDocumentsMode;
  childNames?: string[];
}): Promise<IntegrationSetupResult> {
  if (mode === 'google_drive') {
    if (!isGoogleDriveConfigured()) {
      throw new Error('Google Drive is not configured in this build yet.');
    }

    const structure = await ensureAdultingDriveStructure({ childNames });
    const nextSettings = await saveIntegrationSettings(userId, {
      documents_mode: 'google_drive',
      documents_root_folder_id: structure.rootFolder.id,
      documents_root_folder_name: structure.rootFolder.name,
      documents_root_folder_url: structure.rootFolder.webViewLink || null,
      documents_last_synced_at: new Date().toISOString(),
      documents_sync_status: 'ready',
      google_account_email: structure.userEmail,
    });

    return {
      settings: nextSettings,
      notice: {
        tone: 'success',
        message: 'Google Drive is connected. Adulting can now mirror new documents there while keeping the Adulting catalogue in sync here.',
      },
    };
  }

  if (mode === 'icloud') {
    const settings = await resolveCurrentSettings(userId);
    const nextSettings = await saveIntegrationSettings(userId, {
      documents_mode: 'icloud',
      documents_root_folder_id: settings.documents_mode === 'icloud' ? settings.documents_root_folder_id : null,
      documents_root_folder_name: settings.documents_mode === 'icloud' ? settings.documents_root_folder_name : null,
      documents_root_folder_url: null,
      documents_sync_status: settings.documents_last_synced_at ? settings.documents_sync_status || 'idle' : 'idle',
      google_account_email: resolveGoogleAccountEmail(settings, 'icloud', settings.calendar_mode),
    });

    return {
      settings: nextSettings,
      notice: {
        tone: 'info',
        message: 'Use the Adulting iPhone app to copy Adulting-stored documents into Apple Files when you want that extra file path.',
      },
    };
  }

  const settings = await resolveCurrentSettings(userId);
  const nextSettings = await saveIntegrationSettings(userId, {
    documents_mode: 'adulting',
    documents_root_folder_id: null,
    documents_root_folder_url: null,
    documents_sync_status: 'idle',
    google_account_email: resolveGoogleAccountEmail(settings, 'adulting', settings.calendar_mode),
  });

  return {
    settings: nextSettings,
    notice: {
      tone: 'success',
      message: 'New documents will stay securely in Adulting and sync across your Adulting devices.',
    },
  };
}

export async function updateSetupCalendarMode({
  userId,
  mode,
  currentSettings,
}: {
  userId: string;
  mode: SetupCalendarMode;
  currentSettings?: IntegrationSettingsRecord | null;
}): Promise<IntegrationSetupResult> {
  if (mode === 'google_calendar') {
    if (!isGoogleCalendarConfigured()) {
      throw new Error('Google Calendar is not configured in this build yet.');
    }

    const settings = await resolveCurrentSettings(userId, currentSettings);
    const calendar = await ensureAdultingGoogleCalendar(settings.calendar_external_id || null);
    const nextSettings = await saveIntegrationSettings(userId, {
      calendar_mode: 'google_calendar',
      calendar_external_id: calendar.calendarId,
      calendar_external_name: calendar.calendarName,
      calendar_external_url: calendar.calendarUrl,
      calendar_sync_status: 'ready',
      google_account_email: calendar.userEmail || settings.google_account_email || null,
    });

    return {
      settings: nextSettings,
      notice: {
        tone: 'success',
        message: 'Google Calendar is connected. Adulting can keep the main calendar view here and push copies there when you sync.',
      },
    };
  }

  if (mode === 'icloud') {
    const settings = await resolveCurrentSettings(userId, currentSettings);
    const nextSettings = await saveIntegrationSettings(userId, {
      calendar_mode: 'icloud',
      calendar_external_id: settings.calendar_mode === 'icloud' ? settings.calendar_external_id : null,
      calendar_external_name: settings.calendar_mode === 'icloud' ? settings.calendar_external_name || 'Adulting LifeOS' : 'Adulting LifeOS',
      calendar_external_url: null,
      calendar_sync_status: settings.calendar_last_synced_at ? settings.calendar_sync_status || 'idle' : 'idle',
      google_account_email: resolveGoogleAccountEmail(settings, settings.documents_mode, 'icloud'),
    });

    return {
      settings: nextSettings,
      notice: {
        tone: 'success',
        message: 'Apple Calendar is ready. Open the Adulting iPhone app to sync an Adulting calendar copy there.',
      },
    };
  }

  const settings = await resolveCurrentSettings(userId, currentSettings);
  const nextSettings = await saveIntegrationSettings(userId, {
    calendar_mode: 'adulting',
    calendar_external_id: null,
    calendar_external_url: null,
    calendar_sync_status: 'idle',
    google_account_email: resolveGoogleAccountEmail(settings, settings.documents_mode, 'adulting'),
  });

  return {
    settings: nextSettings,
    notice: {
      tone: 'success',
      message: 'Adulting will keep the main calendar view here, with no external calendar connected.',
    },
  };
}
